import { warehouseLabel } from '@/lib/warehouseLabel';
import { useGT } from 'gt-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MultiSelect,
  type MultiSelectOption,
} from 'src/components/common/MultiSelect';
import type { GetDemandFilterOptionsResponse } from 'src/api/rpc/hooks/procurement/useDemandForecast';
import {
  formatMonthLabel,
  idsOrUndefined,
  selectedOptions,
  type DemandSearch,
  type FilterKey,
} from './demandFilters';
import { useStockStatusLabel } from './demandFilterLabels';

/**
 * A picker's options plus any selected value they no longer list (a URL
 * carried from another screen or an older run). Without it the value still
 * filters but has no checkbox, so it cannot be unchecked and Deselect All
 * leaves it on.
 */
function withSelected(
  all: MultiSelectOption[],
  values: string[] | undefined,
): MultiSelectOption[] {
  const listed = new Set(all.map((option) => option.id));
  const missing = (values ?? []).filter((id) => !listed.has(id));
  return missing.length === 0
    ? all
    : [...all, ...missing.map((id) => ({ id, label: id }))];
}

/** Options for a dimension whose values are their own labels. */
function plainOptions(values: string[]): MultiSelectOption[] {
  return values.map((value) => ({ id: value, label: value }));
}

/**
 * Shared shape of the two free-text filters, so SKU and Text cannot drift apart
 * in behaviour — the only thing that differs is which columns they search.
 */
function SearchInput({
  value,
  onChange,
  onCommit,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  onCommit: (value: string | undefined) => void;
  placeholder: string;
}) {
  return (
    <Input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onBlur={() => onCommit(value || undefined)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onCommit(value || undefined);
      }}
      placeholder={placeholder}
      className="w-48 max-w-full text-xs md:text-xs"
    />
  );
}

interface Props {
  filterKey: FilterKey;
  search: DemandSearch;
  options: GetDemandFilterOptionsResponse | undefined;
  /** Already resets paging — see the bar's `apply`. */
  apply: (next: DemandSearch) => void;
  /** Uncommitted text for whichever free-text filter this is. */
  draft: string;
  onDraftChange: (value: string) => void;
}

/**
 * The control for one filter. Split out of the bar so each is a small, obvious
 * unit; the bar itself only decides which ones are on screen.
 */
export function DemandFilterControl({
  filterKey,
  search,
  options,
  apply,
  draft,
  onDraftChange,
}: Props) {
  const gt = useGT();
  const stockStatusLabel = useStockStatusLabel();
  const anyLabel = gt('Any', { context: 'demand filter placeholder' });

  switch (filterKey) {
    case 'divisionIds': {
      const all: MultiSelectOption[] = (options?.divisions ?? []).map(
        (division) => ({
          id: division.id,
          label: division.name ?? division.id,
        }),
      );
      return (
        <MultiSelect
          options={withSelected(all, search.divisionIds)}
          value={selectedOptions(search.divisionIds, all)}
          onChange={(next) =>
            apply({
              ...search,
              divisionIds: idsOrUndefined(next),
              // Warehouses outside the new division scope would silently cancel
              // it out, so the narrower filter is dropped with it.
              warehouseIds: undefined,
            })
          }
          size="sm"
          className="space-y-0"
          placeholder={anyLabel}
        />
      );
    }
    case 'warehouseIds': {
      // Narrowed to the chosen divisions: offering all two hundred when a
      // division is already picked is a list nobody can use.
      const inScope = new Set(search.divisionIds ?? []);
      const all: MultiSelectOption[] = [];
      for (const warehouse of options?.warehouses ?? []) {
        if (
          inScope.size > 0 &&
          (warehouse.divisionId === null || !inScope.has(warehouse.divisionId))
        ) {
          continue;
        }
        all.push({
          id: warehouse.id,
          label: warehouse.name
            ? warehouseLabel({
                name: warehouse.name,
                displayCode: warehouse.displayCode,
              })
            : (warehouse.displayCode ?? warehouse.id),
        });
      }
      return (
        <MultiSelect
          options={withSelected(all, search.warehouseIds)}
          value={selectedOptions(search.warehouseIds, all)}
          onChange={(next) =>
            apply({ ...search, warehouseIds: idsOrUndefined(next) })
          }
          size="sm"
          className="space-y-0"
          placeholder={anyLabel}
        />
      );
    }
    case 'warehouseTypes': {
      const all = plainOptions(options?.warehouseTypes ?? []);
      return (
        <MultiSelect
          options={withSelected(all, search.warehouseTypes)}
          value={selectedOptions(search.warehouseTypes, all)}
          onChange={(next) =>
            apply({ ...search, warehouseTypes: idsOrUndefined(next) })
          }
          size="sm"
          className="space-y-0"
          placeholder={anyLabel}
        />
      );
    }
    // Two boxes, same behaviour, different scope: SKU searches the item code,
    // Text searches the code or its description. Both commit on Enter or blur
    // rather than per keystroke, because each commit is a request against
    // several million forecast rows.
    case 'skuSearch':
      return (
        <SearchInput
          value={draft}
          onChange={onDraftChange}
          onCommit={(value) => apply({ ...search, skuSearch: value })}
          placeholder={gt('SKU code, * matches anything', {
            context: 'demand filter placeholder',
          })}
        />
      );
    case 'textSearch':
      return (
        <SearchInput
          value={draft}
          onChange={onDraftChange}
          onCommit={(value) => apply({ ...search, textSearch: value })}
          placeholder={gt('SKU or description, * matches anything', {
            context: 'demand filter placeholder',
          })}
        />
      );
    case 'stockStatuses': {
      const all = (options?.stockStatuses ?? []).map((value) => ({
        id: value,
        label: stockStatusLabel(value),
      }));
      return (
        <MultiSelect
          options={withSelected(all, search.stockStatuses)}
          value={selectedOptions(search.stockStatuses, all)}
          onChange={(next) =>
            apply({ ...search, stockStatuses: idsOrUndefined(next) })
          }
          size="sm"
          className="space-y-0"
          placeholder={anyLabel}
        />
      );
    }
    case 'popularityCodes': {
      const all = plainOptions(options?.popularityCodes ?? []);
      return (
        <MultiSelect
          options={withSelected(all, search.popularityCodes)}
          value={selectedOptions(search.popularityCodes, all)}
          onChange={(next) =>
            apply({ ...search, popularityCodes: idsOrUndefined(next) })
          }
          size="sm"
          className="space-y-0"
          placeholder={anyLabel}
        />
      );
    }
    case 'demandClasses': {
      const all = plainOptions(options?.demandClasses ?? []);
      return (
        <MultiSelect
          options={withSelected(all, search.demandClasses)}
          value={selectedOptions(search.demandClasses, all)}
          onChange={(next) =>
            apply({ ...search, demandClasses: idsOrUndefined(next) })
          }
          size="sm"
          className="space-y-0"
          placeholder={anyLabel}
        />
      );
    }
    case 'methods': {
      const all = plainOptions(options?.methods ?? []);
      return (
        <MultiSelect
          options={withSelected(all, search.methods)}
          value={selectedOptions(search.methods, all)}
          onChange={(next) =>
            apply({ ...search, methods: idsOrUndefined(next) })
          }
          size="sm"
          className="space-y-0"
          placeholder={anyLabel}
        />
      );
    }
    case 'leadTimeSources': {
      const all = plainOptions(options?.leadTimeSources ?? []);
      return (
        <MultiSelect
          options={withSelected(all, search.leadTimeSources)}
          value={selectedOptions(search.leadTimeSources, all)}
          onChange={(next) =>
            apply({ ...search, leadTimeSources: idsOrUndefined(next) })
          }
          size="sm"
          className="space-y-0"
          placeholder={anyLabel}
        />
      );
    }
    case 'overrideOnly':
      return (
        <Select
          value={search.overrideOnly === true ? 'yes' : 'any'}
          onValueChange={(value) =>
            apply({
              ...search,
              overrideOnly: value === 'yes' ? true : undefined,
            })
          }
        >
          <SelectTrigger className="w-28 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">{anyLabel}</SelectItem>
            <SelectItem value="yes">
              {gt('Edited only', { context: 'demand filter option' })}
            </SelectItem>
          </SelectContent>
        </Select>
      );
    case 'monthRange': {
      const months = options?.months ?? [];
      return (
        <div className="flex items-center gap-1">
          <Select
            value={search.monthFrom ?? months[0] ?? ''}
            onValueChange={(value) => apply({ ...search, monthFrom: value })}
          >
            <SelectTrigger className="w-24 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((month) => (
                <SelectItem key={month} value={month}>
                  {formatMonthLabel(month)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">→</span>
          <Select
            value={search.monthTo ?? months[months.length - 1] ?? ''}
            onValueChange={(value) => apply({ ...search, monthTo: value })}
          >
            <SelectTrigger className="w-24 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {months.map((month) => (
                <SelectItem key={month} value={month}>
                  {formatMonthLabel(month)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
  }
}
